import { InfoList } from '@orobix/orobix-fe-components'
import { FormikErrors } from 'formik'
import {
  any,
  countBy,
  length,
  map,
  not,
  pipe,
  uniq,
  values as mapToValues,
} from 'ramda'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { DatasetSelectField } from '../../components/DatasetSelect'
import ObservationsSelectField from '../../components/ObservationsSelectField/ObservationsSelectField'
import { USER_LEVEL } from '../../components/Settings/definitions'
import { SettingsContext } from '../../components/Settings/SettingsContext'
import { useStore } from '../../store/store'
import { createTraining, Metadata } from '../../store/storeActions'
import { BackboneSelectOption } from '../../types/backbone'
import { ObservationCompact } from '../../types/observation'
import { getClassesSummary } from '../../utils/forms'
import BackboneSelect from './BackboneSelect/BackboneSelect'
import { MAX_TRAINING_IMAGES } from './const'
import FP16Switch from './FP16Switch'
import TrainingNewForm, { TrainingNewFormValues } from './TrainingNewForm'
import { getEstimatedTrainingsDuration, ITrainingDuration } from './utils'

export type ClassificationFormValues = TrainingNewFormValues

interface RouteParams {
  projectId: string
  productId: string
}

const requiredImages = 5
const requiredImagesPerClass = 5

const NewClassificationTraining = () => {
  const { t } = useTranslation()
  const { product } = useStore()
  const { settings } = useContext(SettingsContext)
  const history = useHistory()
  const { productId, projectId } = useParams<RouteParams>()
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])
  const [estimatedTrainingsDuration, setEstimatedTrainingsDuration] = useState<
    ITrainingDuration[]
  >([])
  const [
    hasNonAnnotatedImagesInDataset,
    setHasNonAnnotatedImagesInDataset,
  ] = useState(false)

  return product ? (
    <TrainingNewForm<ClassificationFormValues>
      productType={product.type}
      productId={productId}
      requiredImages={requiredImages}
      initialValues={{
        selectedObservations: [],
        name: '',
        notes: '',
        startTest: false,
        fp16: true,
      }}
      renderRightColumn={({ values }) => (
        <>
          <InfoList
            list={[
              ...getClassesSummary(values.selectedObservations, t).map((i) => ({
                title: i.title || t('non-annotated'),
                value: i.count,
              })),
              {
                title: t('Total images'),
                value: values.selectedObservations.length,
              },
            ]}
          />

          {settings.preference__user_level === USER_LEVEL.expert && (
            <DatasetSelectField
              fieldName="dataset"
              productId={Number(productId)}
              onSelectedDatasets={setSelectedDatasets}
            />
          )}

          <BackboneSelect fieldName="backbone" productId={product.id} />

          <FP16Switch fieldName="fp16" />
        </>
      )}
      validate={(values) => {
        const errors: FormikErrors<{ [field: string]: string }> = {}

        // for simplicity, we assume that all product classes that belongs to an
        // observation refers to one logical class (thus, we join `productClasses)
        // list to a single string (logical class)
        const productClasses = pipe(
          map((o: ObservationCompact) => o.productClasses),
          map((classes: string[]) => classes.join(', '))
        )(values.selectedObservations).filter((x: string) => x !== '')

        if (
          values.selectedObservations.some((o) => o.productClasses.length === 0)
        ) {
          errors.selectedObservations = t('Cannot use non-annotated images')
        }

        let actualRequiredImagesPerClass = requiredImagesPerClass
        if (values.backbone !== undefined) {
          const backbones: BackboneSelectOption[] = (values.backbone as unknown) as BackboneSelectOption[]
          backbones.forEach((b: BackboneSelectOption) => {
            if (
              b.metadata.requiredImagesPerClass &&
              actualRequiredImagesPerClass < b.metadata.requiredImagesPerClass
            ) {
              actualRequiredImagesPerClass = b.metadata.requiredImagesPerClass
            }
            if (b.code === 'smartcam_classification' && values.fp16) {
              errors.fp16 = t(
                'The {{ precision }} model precision and the selected backbone are not compatible. Please change selection.',
                { precision: 'FP16' }
              )
            }
          })
        }

        const atLeastMinimunRequiredObservationsPerClass = pipe(
          countBy((x: string) => x),
          mapToValues,
          any((x: number) => x < actualRequiredImagesPerClass),
          not
        )(productClasses)

        const numOfSelectedClasses = pipe(uniq, length)(productClasses)

        const requiredClasses = product.productClasses.length

        const len = values.selectedObservations.length
        if (len < requiredImages) {
          errors.selectedObservations = t(
            'You need to use at least {{ count }} image',
            { count: requiredImages }
          )
        } else if (len > MAX_TRAINING_IMAGES) {
          errors.selectedObservations = t(
            'To avoid long training times, you can select up to {{ count }} images',
            { count: MAX_TRAINING_IMAGES }
          )
        }

        if (!atLeastMinimunRequiredObservationsPerClass) {
          errors.selectedObservations = t(
            'You need to use at least {{ count }} image per class',
            { count: actualRequiredImagesPerClass }
          )
        }

        const numMissingClasses = requiredClasses - numOfSelectedClasses
        if (numMissingClasses > 0) {
          errors.selectedObservations = `${t(
            'You need to use at least {{ count }} image per class',
            { count: actualRequiredImagesPerClass }
          )}. ${
            numMissingClasses === 1 ? t('Missing class') : t('Missing classes')
          }: ${product.productClasses
            .filter((i) => !productClasses.includes(i))
            .join(', ')}.`
        }

        if (!values.backbone || values.backbone.length === 0) {
          errors.backbone = t('This field is required')
        }

        if (!values.name) {
          errors.name = t('This field is required')
        }

        if (
          values.startTest &&
          values.selectedObservations.length === product.observationsNumber
        ) {
          errors.startTest = t(
            'Deselect at least one image if you want to start a test with unselected images. Otherwise, deselect this option.'
          )
        }

        const trainingDurations = getEstimatedTrainingsDuration({
          productType: product.type,
          numOfImages: values.selectedObservations.length,
          backbones: values.backbone?.map((b) => ({
            code: b.code,
            label: b.label,
          })),
          precision: values.fp16 ? 'fp16' : 'fp32',
        })

        if (Object.keys(errors).length === 0) {
          setEstimatedTrainingsDuration(trainingDurations)
        }

        if (
          trainingDurations.find((d) => d.sec > 240 * 60) // 4 hours
        ) {
          errors.selectedObservations = t(
            'The estimated training time is too long. Please select fewer images or lower the resolution (if possible).'
          )
        }

        return errors
      }}
      estimatedTrainingsDuration={estimatedTrainingsDuration}
      hasNonAnnotatedImagesInDataset={hasNonAnnotatedImagesInDataset}
      onSubmit={(values, setSubmitting) => {
        if (values.backbone !== undefined) {
          const backbones: BackboneSelectOption[] = (values.backbone as unknown) as BackboneSelectOption[]
          backbones.forEach((b: BackboneSelectOption) => {
            let metadata: Metadata | undefined
            if (values.fp16) metadata = { precision: 'fp16' }
            createTraining(
              Number(projectId),
              Number(productId),
              Number(b.id),
              values.selectedObservations.map((o) => o.id),
              setSubmitting,
              history.push,
              metadata,
              values.name,
              values.notes,
              values.startTest,
              selectedDatasets.length > 0
                ? selectedDatasets.map((id) => Number(id))
                : undefined
            )
          })
        }
      }}
    >
      {/* overrides the original ObservationSelectField to handle dataset */}
      <ObservationsSelectField
        name="selectedObservations"
        productType={product.type}
        productId={Number(productId)}
        dataset={selectedDatasets.length > 0 ? selectedDatasets : undefined}
        inTraining
        onNonAnnotatedImagesInDataset={setHasNonAnnotatedImagesInDataset}
      />
    </TrainingNewForm>
  ) : null
}

export default NewClassificationTraining
